import { clearAllBodyScrollLocks, disableBodyScroll } from "body-scroll-lock";
import classNames from "classnames";
import React, { useCallback, useContext, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Modal from "react-modal";

import { ThemeContext } from "../../contexts/ThemeContext";
import { getHighlightElement } from "../../lib/intl-utils";
import APIClient from "../../utils/api-client";
import FieldErrors from "../account/FieldErrors";

export const TEST_ID_RENEWAL_MODAL_TITLE = "season-pass-renewal-modal-title";
export const TEST_ID_RENEWAL_MODAL_TICKET_ID_INPUT =
  "season-pass-renewal-modal-ticket-id-input";
export const TEST_ID_RENEWAL_MODAL_VERIFICATION_CODE_INPUT =
  "season-pass-renewal-modal-verification-code-input";

const OVERLAY_CLASSNAME = "confirm-overlay";

interface SeasonPassRenewalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRenewalData: (
    holder: Paths.FetchSeasonCardHolder.Responses.$200,
    ticketId: string,
  ) => void;
}

function onAfterOpen() {
  const [overlay] = document.getElementsByClassName(OVERLAY_CLASSNAME);
  if (overlay) {
    disableBodyScroll(overlay, {
      reserveScrollBarGap: true,
    });
  }
}

const apiClient = new APIClient();

const SeasonPassRenewalModal: React.FC<SeasonPassRenewalModalProps> = ({
  isOpen,
  onClose,
  onRenewalData,
}) => {
  const { formatMessage, locale } = useIntl();
  const { styles } = useContext(ThemeContext);
  const [ticketId, setTicketId] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [isSending, setSending] = useState(false);
  const [hotpError, setHotpError] = useState<string | undefined>(undefined);
  const [verifyError, setVerifyError] = useState<string | undefined>(undefined);
  const [emailSent, setEmailSent] = useState(false);

  const handleClose = useCallback(() => {
    setTicketId("");
    setSending(false);
    setHotpError(undefined);
    setEmailSent(false);
    onClose();
  }, [onClose]);

  const onSendClick = useCallback(async () => {
    setHotpError(undefined);
    setSending(true);

    try {
      const { status } = await apiClient.requestTicketHOTP(ticketId);

      switch (status) {
        case 204: {
          setEmailSent(true);
          break;
        }
        case 404: {
          setHotpError("hotp.error.seasonCardNotFound");
          break;
        }
        default: {
          setHotpError("hotp.error.unexpected");
          break;
        }
      }
    } catch {
      setHotpError("hotp.error.unexpected");
    } finally {
      setSending(false);
    }
  }, [ticketId]);

  const onVerifyClick = useCallback(async () => {
    setVerifyError(undefined);
    setSending(true);

    try {
      const { status, holder } = await apiClient.fetchSeasonCardHolder(
        ticketId,
        verificationCode,
      );

      switch (status) {
        case 200: {
          if (holder) {
            onRenewalData(holder, ticketId);
          } else {
            setVerifyError("hotp.error.unexpected");
          }
          break;
        }
        case 403: {
          setVerifyError("hotp.error.invalid");
          break;
        }
        default: {
          setVerifyError("hotp.error.unexpected");
        }
      }
    } catch {
      setVerifyError("hotp.error.unexpected");
    } finally {
      setSending(false);
    }
  }, [onRenewalData, ticketId, verificationCode]);

  const sendDisabled = ticketId.length === 0 || isSending;
  const verifyDisabled = sendDisabled || verificationCode.length === 0;
  const { origin } = location;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className="outline-0 br-1 bg-white pa4 modal-content"
      onAfterOpen={onAfterOpen}
      onAfterClose={clearAllBodyScrollLocks}
      overlayClassName={OVERLAY_CLASSNAME}
    >
      <h2
        className={classNames(styles.heading2)}
        data-testid={TEST_ID_RENEWAL_MODAL_TITLE}
      >
        <FormattedMessage id="seasonpass.renewal.title" />
      </h2>
      <p>
        <FormattedMessage
          id="seasonpass.renewal.description"
          values={{
            strong: getHighlightElement,
            cardURL: `${origin}/${locale}/kausikortti`,
          }}
        />
      </p>
      <label className={classNames(styles.heading3, "ttn")}>
        <FormattedMessage id="forms.seasonpass.ticketId" />
        <input
          className={classNames(styles.input, "db mt3 w-100 pa0 br0")}
          type="text"
          name="ticketId"
          autoComplete="off"
          placeholder={formatMessage({
            id: "forms.seasonpass.ticketId.placeholder",
          })}
          onChange={event =>
            setTicketId(event.target.value.toUpperCase().trim())
          }
          readOnly={emailSent}
          value={ticketId}
          data-testid={TEST_ID_RENEWAL_MODAL_TICKET_ID_INPUT}
          data-lpignore="true"
        />
        <FieldErrors value={hotpError} touched />
      </label>
      {!emailSent && (
        <button
          disabled={sendDisabled}
          className={classNames(
            {
              [styles.secondaryButton]: !sendDisabled,
              [styles.secondaryButtonDisabled]: sendDisabled,
            },
            "mt4",
          )}
          onClick={onSendClick}
        >
          <FormattedMessage id="seasonpass.renewal.sendVerificationCode" />
        </button>
      )}
      {emailSent && (
        <div>
          <p className="pink mv4">
            <strong>
              <FormattedMessage id="seasonpass.renewal.verificationDescription" />
            </strong>
          </p>
          <label className={classNames(styles.heading3, "ttn")}>
            <FormattedMessage id="forms.seasonpass.verificationCode" />
            <input
              className={classNames(styles.input, "db mt3 w-100 pa0 br0")}
              type="text"
              name="verificationCode"
              autoComplete="off"
              placeholder={formatMessage({
                id: "forms.seasonpass.verificationCode",
              })}
              onChange={event =>
                setVerificationCode(event.target.value.toUpperCase().trim())
              }
              value={verificationCode}
              data-testid={TEST_ID_RENEWAL_MODAL_VERIFICATION_CODE_INPUT}
              data-lpignore="true"
            />
            <FieldErrors value={verifyError} touched />
          </label>

          <button
            disabled={verifyDisabled}
            className={classNames(
              {
                [styles.secondaryButton]: !verifyDisabled,
                [styles.secondaryButtonDisabled]: verifyDisabled,
              },
              "mt4",
            )}
            onClick={onVerifyClick}
          >
            <FormattedMessage id="seasonpass.renewal.verify" />
          </button>
        </div>
      )}
    </Modal>
  );
};

export default SeasonPassRenewalModal;
